
import React, { useState, useRef, useCallback, useEffect } from 'react'
import { CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { NodeContainer } from '../base/NodeContainer'
import { withNodeOptimization } from '../hoc/withNodeOptimization'
import { NODE_DIMENSIONS, NODE_VISUAL_CONSTANTS } from '@genieartinc/schemas'
import { Icon } from '@/components/ui/icon'
import { useCanvasFiles } from '@/hooks/useCanvasFiles'
import { useParams } from '@tanstack/react-router'
import { readLibraryFileDrop } from '@/components/canvas/nodes/utils/library-file-drop'
import { useContentAutoSize } from '../base/use-content-auto-size'

interface FileMetadata {
  filename?: string
  publicUrl?: string
  width?: number
  height?: number
  duration?: number
}

interface VideoFileProps {
  id: string
  title?: string
  selected?: boolean
  fileUuid?: string
  canvasId?: string
  onSelect?: () => void
  onHoverChange?: (hovered: boolean) => void
  onFileChange?: (fileUuid: string, metadata: {
    filename: string
    size: number
    contentType: string
    publicUrl: string
  }) => void
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  state?: Record<string, unknown>
  hasParent?: boolean
}

function VideoFileComponent({
  id,
  title,
  selected = false,
  canvasId: propCanvasId,
  onSelect,
  onHoverChange,
  onFileChange,
  onStateChange,
  state = {},
  hasParent = false,
}: VideoFileProps) {
  const { id: canvasIdParam } = useParams({ strict: false })
  const canvasId = propCanvasId || (canvasIdParam as string)

  const [isHovered, setIsHovered] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const isUploadingRef = useRef(false)

  const { uploadFile, isUploading, uploadProgress, error } = useCanvasFiles()

  const fileMetadata = state?.fileMetadata as FileMetadata | undefined
  const videoUrl = fileMetadata?.publicUrl || null
  const reportAspect = useContentAutoSize(id, 'VideoFile')

  useEffect(() => {
    if (state.sizeSource === 'manual') return
    const width = fileMetadata?.width
    const height = fileMetadata?.height
    if (typeof width === 'number' && width > 0 && typeof height === 'number' && height > 0) {
      reportAspect(height / width)
    }
  }, [fileMetadata?.width, fileMetadata?.height, reportAspect, state.sizeSource])

  const handleVideoMetadata = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    if (state.sizeSource === 'manual') return
    const video = event.currentTarget
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      reportAspect(video.videoHeight / video.videoWidth)
    }
  }, [reportAspect, state.sizeSource])

  // Handle file upload
  const uploadVideoFile = useCallback(async (file: File) => {
    if (isUploadingRef.current) return
    isUploadingRef.current = true

    if (!canvasId) {
      isUploadingRef.current = false
      return
    }

    try {
      const uploadedFile = await uploadFile(canvasId, {
        file,
        nodeId: id,
      })

      if (!uploadedFile) {
        throw new Error('Upload failed')
      }

      onFileChange?.(uploadedFile.uuid, {
        filename: uploadedFile.filename,
        size: uploadedFile.size,
        contentType: uploadedFile.contentType,
        publicUrl: uploadedFile.publicUrl || '',
      })
    } catch (err) {
      console.error('Failed to upload video:', err)
    } finally {
      isUploadingRef.current = false
    }
  }, [canvasId, id, uploadFile, onFileChange])

  const handleVideoUpload = useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'video/mp4,video/webm,video/quicktime'

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      await uploadVideoFile(file)
    }

    input.click()
  }, [uploadVideoFile])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const file = e.dataTransfer.files?.[0]
    if (!file) {
      // A file dragged from the File Manager is already uploaded: link it instead.
      const library = readLibraryFileDrop(e.dataTransfer)
      if (library?.contentType.startsWith('video/')) onFileChange?.(library.uuid, { filename: library.filename, size: 0, contentType: library.contentType, publicUrl: library.publicUrl })
      return
    }
    if (!file.type.startsWith('video/')) return

    await uploadVideoFile(file)
  }, [uploadVideoFile, onFileChange])

  const handleVideoRemove = useCallback(() => {
    onFileChange?.('', { filename: '', size: 0, contentType: '', publicUrl: '' })
  }, [onFileChange])

  return (
    <NodeContainer
      nodeId={id}
      title={title || 'Video'}
      icon="video"
      selected={selected}
      width={NODE_DIMENSIONS.VideoFile.width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
    >
      <CardContent
        className={`flex min-h-0 flex-1 flex-col items-stretch justify-center overflow-hidden p-1 transition-colors ${isDragOver ? 'bg-primary/10 border-2 border-primary border-dashed' : ''}`}
        style={{
          borderRadius: `${NODE_VISUAL_CONSTANTS.borderRadius.card}px`,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {(state?.generationStatus as string) === 'loading' ? (
          <div
            className="relative flex flex-col w-full aspect-video box-border items-center justify-center gap-3 rounded-[10px] bg-muted overflow-hidden"
            role="status"
            aria-label="Generating video content"
          >
            <div
              className="absolute inset-0 animate-[shimmer-sweep_2.5s_ease-in-out_infinite]"
              style={{
                background: 'linear-gradient(105deg, transparent 30%, rgba(239,68,68,0.08) 45%, rgba(239,68,68,0.15) 50%, rgba(239,68,68,0.08) 55%, transparent 70%)',
                backgroundSize: '200% 100%',
              }}
            />
            <span className="relative text-xs font-medium text-muted-foreground">
              {(state?.generationMessage as string) || 'Generating video...'}
            </span>
            <span className="relative text-[10px] tabular-nums text-muted-foreground" aria-live="polite" role="status">
              {Math.round((state?.generationProgress as number) ?? 0)}%
            </span>
          </div>
        ) : (state?.generationStatus as string) === 'error' ? (
          <div className="relative flex flex-col w-full aspect-video box-border items-center justify-center gap-2 rounded-[10px] bg-destructive/10 overflow-hidden">
            <Icon name="alert-circle" className="h-6 w-6 text-destructive" />
            <span className="text-xs text-destructive">Generation failed</span>
          </div>
        ) : videoUrl ? (
          <div className="relative min-h-0 w-full flex-1">
            <video
              src={videoUrl}
              controls
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={handleVideoMetadata}
              className="absolute inset-0 h-full w-full object-contain"
              style={{
                borderRadius: '10px',
              }}
            />

            {/* Loading overlay */}
            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-[10px]">
                <Icon name="loader-circle" className="h-8 w-8 text-white animate-spin" />
              </div>
            )}

            {/* Hover buttons */}
            {isHovered && !isUploading && (
              <div className="absolute top-2 right-2 flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 rounded-[6px] p-0 bg-card hover:bg-accent"
                  onClick={async (e) => {
                    e.stopPropagation()
                    try {
                      const downloadUrl = videoUrl
                      if (!downloadUrl) return
                      const response = await fetch(downloadUrl)
                      const blob = await response.blob()
                      const url = window.URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = fileMetadata?.filename || `video-${id}.mp4`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      window.URL.revokeObjectURL(url)
                    } catch (err) {
                      console.error('Failed to download video:', err)
                    }
                  }}
                  title="Download video"
                  aria-label="Download video"
                >
                  <Icon name="download" className="h-4 w-4" />
                </Button>

                {!hasParent && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 rounded-[6px] p-0 bg-card hover:bg-accent"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleVideoUpload()
                      }}
                      title="Replace video"
                      aria-label="Replace video"
                    >
                      <Icon name="upload" className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 rounded-[6px] p-0 bg-card hover:bg-accent"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleVideoRemove()
                      }}
                      title="Remove video"
                      aria-label="Remove video"
                    >
                      <Icon name="x" className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleVideoUpload()
            }}
            disabled={isUploading}
            className="flex flex-col w-full aspect-video box-border items-center justify-center gap-3 border-2 border-dashed border-border transition-colors hover:border-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              borderRadius: `${NODE_VISUAL_CONSTANTS.borderRadius.button.large}px`,
            }}
          >
            {isUploading ? (
              <>
                <Icon name="loader-circle" className="h-6 w-6 text-muted-foreground animate-spin" />
                <div className="flex flex-col items-center gap-1">
                  <span className="text-sm text-muted-foreground">Uploading...</span>
                  {uploadProgress !== null && (
                    <span className="text-xs text-muted-foreground">{uploadProgress}%</span>
                  )}
                </div>
              </>
            ) : (
              <>
                <Icon name="upload" className="h-6 w-6 text-muted-foreground" />
                <div className="flex flex-col items-center gap-1">
                  <span className="text-sm text-muted-foreground">
                    {error ? 'Upload failed. Click to retry' : 'Drag and drop your video here'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    MP4, WebM, MOV. Up to 250mb.
                  </span>
                </div>
              </>
            )}
          </button>
        )}
      </CardContent>
    </NodeContainer>
  )
}

export const VideoFile = withNodeOptimization(VideoFileComponent, {
  compareProps: ['id', 'title', 'selected', 'fileUuid', 'canvasId', 'state']
})
